import React, { useState, useEffect } from "react";
import Layout from "../../Layout";
import axios from "axios"; // If you choose to use axios
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import TaggedImageUploader, { toUploadFormData } from "./TaggedImageUploader";

const ImageUploadEdit = () => {
  const { fetchProject } = useOutletContext();
  const [formData, setFormData] = useState({ title: "", uploads: [] }); // uploads: [{ file, tag }]
  const [errors, setErrors] = useState({ title: null, projectImages: null });
  const [isUploading, setIsUploading] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACK_URL}/api/project-showcases/${id}`,
        );
        setFormData((prev) => ({
          ...prev,
          title: response.data.title,
        }));
      } catch (error) {
        console.error("Failed to fetch project data", error);
      }
    };
    fetchProject();
  }, [id]);
  const handleUploadsChange = (uploads) => {
    setFormData((prev) => ({ ...prev, uploads }));
    setErrors((prev) => ({ ...prev, projectImages: null }));
  };

  const handleTitleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      title: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (isUploading) return;

    if (formData.title.trim() === "") {
      setErrors((prev) => ({
        ...prev,
        title: "Please enter a project title.",
      }));
      toast.error("Please enter a project title.");
      return;
    }

    if (formData.uploads.length === 0) {
      setErrors((prev) => ({
        ...prev,
        projectImages: "Please upload at least one image.",
      }));
      toast.error("Please upload at least one image.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    toUploadFormData(formDataToSend, formData.uploads);

    setIsUploading(true);
    const toastId = toast.loading("Uploading images...");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/re-upload/${id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: localStorage.getItem("usersdatatoken"),
          },
        },
      );
      toast.update(toastId, {
        render: "Images uploaded successfully!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      if (fetchProject) {
        fetchProject();
      }
      setFormData((prev) => ({ ...prev, uploads: [] }));
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.update(toastId, {
        render:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong during image upload.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <div className="p-3 mx-auto container bg-slate-50">
        <h1 className="text-2xl mt-3 mb-8">Re-Upload Project Images</h1>
        <div className="flex flex-col px-4 pt-4 pb-5 rounded-lg bg-white mb-4 border">
          <div>
            <input
              className="py-2 px-3 border w-full rounded-lg outline-primary-500"
              value={formData.title}
              readOnly
              disabled
              onChange={handleTitleChange}
              placeholder="Enter project title..."
            />
            {errors.title && (
              <p className="text-red-500 text-xs px-2 pt-1">{errors.title}</p>
            )}
          </div>
          <TaggedImageUploader
            uploads={formData.uploads}
            onChange={handleUploadsChange}
          />
          {errors.projectImages && (
            <p className="text-red-500 text-xs px-2 pt-1">
              {errors.projectImages}
            </p>
          )}
        </div>
        <div className="flex gap-3 justify-end items-center">
          <button
            type="button"
            className="bg-green-500 hover:bg-green-600 py-2 px-4 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition"
            onClick={handleSubmit}
            disabled={isUploading}
          >
            {isUploading && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            {isUploading ? "Uploading..." : "Upload Images"}
          </button>
          <Link
            to={isUploading ? "#" : `/project-showcase/${id}/edit/projects`}
            onClick={(e) => {
              if (isUploading) e.preventDefault();
            }}
          >
            <button
              type="button"
              className="bg-red-500 hover:bg-red-600 py-2 px-4 rounded-lg text-white font-medium flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition"
              disabled={isUploading}
            >
              Done
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadEdit;
